import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';
import { EmpresaStateService } from '../../services/empresa-state.service';
import { Subscription, interval } from 'rxjs';

export interface LogisticaEstoqueLinha {
  nunota:      number;
  dtneg?:      string;
  prioridade:  string;
  imediato:    string;
  nomeCliente: string;
  vendedor:    string;
  prefixo?:    string;
  confNome?:   string;
  dataFimConf?: string;
  totalItens:  number;
  numTag?:     string;
}

@Component({
  selector: 'app-estoque-logistica',
  standalone: true,
  imports: [LoadingIndicatorComponent, CommonModule, FormsModule],
  templateUrl: './estoque-logistica.component.html',
  styleUrls: ['./estoque-logistica.component.css']
})
export class EstoqueLogisticaComponent implements OnInit, OnDestroy {
  pedidos: LogisticaEstoqueLinha[] = [];
  carregando = false;
  erro = '';
  private sub?: Subscription;
  private autoRefreshSub?: Subscription;
  private _empresaSub?: Subscription;

  @Output() visualizarItens = new EventEmitter<number>();

  // Modal confirmar envio
  modalAberto = false;
  pedidoModal: LogisticaEstoqueLinha | null = null;
  confirmando = false;
  erroConfirmar = '';

  // ── Estornar para CONFERENCIA ──────────────────────────────
  estornandoNunota: number | null = null;
  erroEstornar = '';
  confirmandoEstorno: LogisticaEstoqueLinha | null = null;

  constructor(private gateway: SankhyaGatewayService, readonly empresaState: EmpresaStateService) {}

  ngOnInit(): void {
    this.buscarPedidos(true);
    this.autoRefreshSub = interval(10000).subscribe(() => this.buscarPedidos(false));

    // Isolamento por empresa: recarrega ao trocar a empresa selecionada.
    this._empresaSub = this.empresaState.empresaMudou.subscribe(() => {
      this.buscarPedidos(true);
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.autoRefreshSub?.unsubscribe();
    this._empresaSub?.unsubscribe();
  }

  /** Nome de exibição da empresa atualmente selecionada (badge no cabeçalho). */
  get nomeEmpresaAtual(): string {
    const emp = this.empresaState.opcoes.find(e => e.codEmp === this.empresaState.selecionada);
    return emp?.nomeFant || emp?.razaoAbrev || '';
  }

  buscarPedidos(exibirLoading = false): void {
    if (exibirLoading) {
      this.carregando = true;
      this.erro = '';
    }
    if (this.sub && !this.sub.closed) this.sub.unsubscribe();

    const codEmp = Number(this.empresaState.selecionada ?? 1);
    this.sub = this.gateway.buscarPedidosLogistica(codEmp).subscribe({
      next: (res: any) => {
        this.carregando = false;
        if (res?.sucesso) {
          this.erro = '';
          this.pedidos = res.pedidos as LogisticaEstoqueLinha[];
        } else if (exibirLoading || !this.pedidos.length) {
          this.erro = res?.erro || 'Erro ao carregar pedidos.';
        }
      },
      error: () => {
        this.carregando = false;
        if (exibirLoading || !this.pedidos.length) {
          this.erro = 'Falha de comunicação com o servidor.';
        }
      }
    });
  }

  // ── Modal ─────────────────────────────────────────────────────────────────

  abrirModal(pedido: LogisticaEstoqueLinha): void {
    this.pedidoModal  = pedido;
    this.erroConfirmar = '';
    this.confirmando   = false;
    this.modalAberto   = true;
  }

  fecharModal(): void {
    if (this.confirmando) return;
    this.modalAberto  = false;
    this.pedidoModal  = null;
    this.erroConfirmar = '';
  }

  onVisualizarItens(): void {
    if (!this.pedidoModal) return;
    const nunota = this.pedidoModal.nunota;
    this.fecharModal();
    this.visualizarItens.emit(nunota);
  }

  onConfirmarEnvio(): void {
    if (!this.pedidoModal || this.confirmando) return;
    this.confirmando   = true;
    this.erroConfirmar = '';
    const { nunota, totalItens } = this.pedidoModal;
    this.gateway.confirmarEnvioLogistica(nunota, 'DIRETO', totalItens, 0).subscribe({
      next: (res: any) => {
        this.confirmando = false;
        if (res?.sucesso) {
          this.modalAberto = false;
          this.pedidoModal = null;
          this.buscarPedidos(false);
        } else {
          this.erroConfirmar = res?.erro || 'Erro ao confirmar envio.';
        }
      },
      error: (err: any) => {
        this.confirmando = false;
        this.erroConfirmar = err?.error?.erro ?? err?.message ?? 'Falha de comunicação.';
      }
    });
  }

  /** AOG (vermelho), Urgente (laranja), Alta (vermelho claro) e Normal (verde) --
   *  mesma lógica de cores do Painel de Separação (ver prioridadeClass lá).
   *  Usa "includes" porque o valor cru às vezes vem com prefixo numérico do
   *  Sankhya, ex.: "1-AOG", "3-URGENTE". */
  prioridadeClass(prioridade: string | null | undefined): string {
    const p = (prioridade ?? '').trim().toLowerCase();
    if (p.includes('aog')) return 'badge badge-aog';
    if (p.includes('urgente')) return 'badge badge-urgente';
    if (p.includes('alta')) return 'badge badge-alta';
    return 'badge badge-normal';
  }

  // ── Estornar para CONFERENCIA ────────────────────────────────────────────

  solicitarEstornar(event: Event, pedido: LogisticaEstoqueLinha): void {
    event.stopPropagation();
    if (this.estornandoNunota !== null) return;
    this.confirmandoEstorno = pedido;
    this.erroEstornar = '';
  }

  cancelarEstornar(): void {
    if (this.estornandoNunota !== null) return;
    this.confirmandoEstorno = null;
    this.erroEstornar = '';
  }

  confirmarEstornar(): void {
    const pedido = this.confirmandoEstorno;
    if (!pedido || this.estornandoNunota !== null) return;
    this.estornandoNunota = pedido.nunota;
    this.erroEstornar = '';
    this.gateway.estornarLogistica(pedido.nunota).subscribe({
      next: (res: any) => {
        this.estornandoNunota = null;
        if (res?.sucesso) {
          this.confirmandoEstorno = null;
          this.buscarPedidos(false);
        } else {
          this.erroEstornar = res?.erro || 'Erro ao estornar logística.';
        }
      },
      error: () => {
        this.estornandoNunota = null;
        this.erroEstornar = 'Falha de comunicação com o servidor.';
      }
    });
  }
}
